import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WorkListPage from '../../../components/work/WorkListPage';
import StandardFilterPanel from '../../../components/hr/StandardFilterPanel';
import { projectTemplatesApi, projectSubcategoriesApi } from '../../../utils/workApi';
import {
  parseProjectTemplatesList,
  resolveProjectTemplateCategoryName,
} from '../../../utils/workResponseUtils';
import { PANEL_PRIMARY_BTN_CLASS, BULK_ACTION_BAR_CLASS, BULK_ACTION_BAR_TEXT_CLASS } from '../../../constants/ui';
import { confirmWorkDelete } from '../../../utils/workConfirmModal';
import { Button, Table, Space, message, Dropdown, Tooltip } from 'antd';
import {
  PlusOutlined,
  ArrowRightOutlined,
  EyeOutlined,
  DeleteOutlined,
  ProjectOutlined,
  EditOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { TableSkeleton } from '../../../components/SkeletonLoaders';
import { useListReturnState } from '../../../hooks/useListReturnState';

const label = (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span>;

const ProjectTemplates = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [filters, setFilters] = useState({
    category_id: 'all',
    sub_category_id: 'all',
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const captureCurrentListState = useCallback(
    () => ({
      filters,
      searchText,
      pagination: {
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    }),
    [filters, searchText, pagination.current, pagination.pageSize],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.filters) setFilters(restored.filters);
    if (restored.searchText != null) setSearchText(restored.searchText);
    if (restored.pagination) {
      setPagination((prev) => ({
        current: restored.pagination.current || 1,
        pageSize: restored.pagination.pageSize || prev.pageSize,
      }));
    }
  });

  const loadSubcategories = async (categoryId) => {
    if (!categoryId || categoryId === 'all') {
      setSubcategories([]);
      return;
    }
    try {
      const res = await projectSubcategoriesApi.getByCategory(categoryId);
      setSubcategories(res?.sub_categories || res?.subcategories || res?.data || []);
    } catch {
      setSubcategories([]);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await projectTemplatesApi.getAll({
        page: pagination.current,
        per_page: pagination.pageSize,
        category_id: filters.category_id,
        sub_category_id: filters.sub_category_id,
        search: searchText || undefined,
      });
      const parsed = parseProjectTemplatesList(res);
      setRows(parsed.rows);
      setTotal(parsed.total);
      const cats = res?.data?.categories || res?.categories || [];
      if (Array.isArray(cats) && cats.length) {
        setCategories(cats);
      }
    } catch {
      message.error('خطا در بارگذاری');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination((p) => (p.current === 1 ? p : { ...p, current: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, searchText, filters.category_id, filters.sub_category_id]);

  useEffect(() => {
    loadSubcategories(filters.category_id);
  }, [filters.category_id]);

  const handleBulkDelete = () => {
    if (!selectedRowKeys.length) {
      message.warning('حداقل یک الگو انتخاب کنید');
      return;
    }
    confirmWorkDelete({
      title: 'حذف الگوها',
      content: `آیا از حذف ${selectedRowKeys.length} الگوی انتخاب‌شده اطمینان دارید؟`,
      onOk: async () => {
        setBulkLoading(true);
        try {
          await projectTemplatesApi.applyQuickAction('delete', selectedRowKeys);
          message.success('الگوها حذف شدند');
          setSelectedRowKeys([]);
          load();
        } catch {
          message.error('خطا در حذف گروهی');
        } finally {
          setBulkLoading(false);
        }
      },
    });
  };

  const handleRowDelete = (rowId) => {
    confirmWorkDelete({
      title: 'حذف الگو',
      content: 'آیا از حذف این الگو اطمینان دارید؟',
      onOk: async () => {
        try {
          await projectTemplatesApi.applyQuickAction('delete', [rowId]);
          message.success('الگو حذف شد');
          load();
        } catch {
          message.error('خطا در حذف');
        }
      },
    });
  };

  const goCreateProject = (row) => {
    navigate('/work/projects/add', {
      state: {
        templateId: row.id,
        returnTo: '/work/projects/templates',
      },
    });
  };

  const bulkActionMenuItems = [
    {
      key: 'delete',
      label: label('حذف انتخاب‌شده‌ها'),
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleBulkDelete,
    },
  ];

  const filterDefs = [
    {
      key: 'category_id',
      type: 'select',
      label: 'دسته‌بندی',
      value: filters.category_id === 'all' ? undefined : filters.category_id,
      options: categories.map((c) => ({
        value: c.id,
        label: c.category_name,
      })),
      showSearch: true,
    },
    {
      key: 'sub_category_id',
      type: 'select',
      label: 'زیردسته',
      value: filters.sub_category_id === 'all' ? undefined : filters.sub_category_id,
      options: subcategories.map((s) => ({
        value: s.id,
        label: s.category_name || s.name,
      })),
      showSearch: true,
    },
  ];

  return (
    <WorkListPage
      title="الگوهای پروژه"
      subtitle="مدیریت قالب‌های پروژه"
      icon={ProjectOutlined}
      headerExtra={(
        <Space wrap>
          <Button
            icon={<ArrowRightOutlined />}
            onClick={() => navigate('/work/projects')}
            style={{ fontFamily: 'CustomFont' }}
          >
            بازگشت به پروژه‌ها
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className={PANEL_PRIMARY_BTN_CLASS}
            onClick={() => navigate('/work/projects/templates/new')}
            style={{ fontFamily: 'CustomFont' }}
          >
            الگوی جدید
          </Button>
        </Space>
      )}
    >
      <StandardFilterPanel
        searchValue={searchText}
        onSearchChange={setSearchText}
        onSearch={() => {}}
        searchPlaceholder="جستجو در الگوها..."
        quickFilters={filterDefs}
        advancedFilters={[]}
        onFilterChange={(k, v) => {
          setFilters((prev) => {
            const next = { ...prev, [k]: v ?? 'all' };
            if (k === 'category_id') {
              next.sub_category_id = 'all';
            }
            return next;
          });
          setPagination((p) => ({ ...p, current: 1 }));
        }}
        onResetFilters={() => {
          setFilters({ category_id: 'all', sub_category_id: 'all' });
          setSearchText('');
          setSubcategories([]);
          setPagination((p) => ({ ...p, current: 1 }));
        }}
        totalCount={total}
        showAdvancedFilters={false}
        showTotalCount
      />

      {selectedRowKeys.length > 0 ? (
        <div className={BULK_ACTION_BAR_CLASS}>
          <span className={BULK_ACTION_BAR_TEXT_CLASS} style={{ fontFamily: 'CustomFont' }}>
            {selectedRowKeys.length} مورد انتخاب شده
          </span>
          <Dropdown menu={{ items: bulkActionMenuItems }} trigger={['click']} disabled={bulkLoading}>
            <Button
              type="primary"
              loading={bulkLoading}
              icon={<DownOutlined />}
              className={PANEL_PRIMARY_BTN_CLASS}
              style={{ fontFamily: 'CustomFont' }}
            >
              عملیات گروهی
            </Button>
          </Dropdown>
          <Button type="link" onClick={() => setSelectedRowKeys([])} style={{ fontFamily: 'CustomFont' }}>
            لغو انتخاب
          </Button>
        </div>
      ) : null}

      {loading ? (
        <TableSkeleton rows={5} columns={4} />
      ) : (
        <Table
          rowKey="id"
          dataSource={rows}
          rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
          scroll={{ x: true }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total,
            showSizeChanger: true,
            onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
          }}
          columns={[
            {
              title: label('نام'),
              dataIndex: 'project_name',
              ellipsis: true,
              render: (v, row) => (
                <button
                  type="button"
                  className="text-indigo-600 font-medium bg-transparent border-0 p-0 cursor-pointer"
                  style={{ fontFamily: 'CustomFont' }}
                  onClick={() =>
                    navigateToDetail(`/work/projects/templates/${row.id}`, {}, {
                      listPath: '/work/projects/templates',
                      listLabel: 'قالب‌های پروژه',
                      entityLabel: row.project_name || `قالب #${row.id}`,
                    })
                  }
                >
                  {v || '—'}
                </button>
              ),
            },
            {
              title: label('دسته‌بندی'),
              key: 'category',
              render: (_, row) => (
                <span style={{ fontFamily: 'CustomFont' }}>
                  {resolveProjectTemplateCategoryName(row, categories)}
                </span>
              ),
            },
            {
              title: label('عملیات'),
              key: 'actions',
              fixed: 'right',
              width: 180,
              render: (_, row) => (
                <Space size="small">
                  <Tooltip title="ایجاد پروژه">
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                      onClick={() => goCreateProject(row)}
                    />
                  </Tooltip>
                  <Tooltip title="مشاهده">
                    <Button
                      type="text"
                      icon={<EyeOutlined />}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      onClick={() =>
                    navigateToDetail(`/work/projects/templates/${row.id}`, {}, {
                      listPath: '/work/projects/templates',
                      listLabel: 'قالب‌های پروژه',
                      entityLabel: row.project_name || `قالب #${row.id}`,
                    })
                  }
                    />
                  </Tooltip>
                  <Tooltip title="ویرایش">
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() =>
                        navigateToDetail(`/work/projects/templates/${row.id}/edit`, {}, {
                          listPath: '/work/projects/templates',
                          listLabel: 'قالب‌های پروژه',
                          entityLabel: row.project_name
                            ? `ویرایش ${row.project_name}`
                            : `ویرایش قالب #${row.id}`,
                        })
                      }
                    />
                  </Tooltip>
                  <Tooltip title="حذف">
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRowDelete(row.id)}
                    />
                  </Tooltip>
                </Space>
              ),
            },
          ]}
        />
      )}
    </WorkListPage>
  );
};

export default ProjectTemplates;
